import React from "react";
import {
  FaCalculator,
  FaFileMedicalAlt,
  FaProjectDiagram,
  FaTable,
  FaUsers,
} from "react-icons/fa";
import { MdFunctions, MdTrendingUp } from "react-icons/md";

const methodItems = [
  {
    icon: FaCalculator,
    title: "Cancer Rates",
    text: "Cancer rates are calculated to compare data across regions and populations. These are usually expressed per 10,00,000 people to ensure uniform analysis.",
  },
  {
    icon: FaFileMedicalAlt,
    title: "Cancer Case Definition",
    text: "All cancer cases recorded in PBCCR follow internationally accepted medical classification standards. Only confirmed cases are included to ensure data reliability and consistency.",
  },
  {
    icon: FaUsers,
    title: "Age Group Classification",
    text: "Cancer data is categorized into different age groups to understand patterns across populations. According to standard guidelines, childhood cancer is defined as cases occurring between ages 0-19 years.",
  },
  {
    icon: MdTrendingUp,
    title: "Cancer Incidence",
    text: "Incidence refers to the number of new cancer cases diagnosed within a specific population during a defined time period. It helps in identifying trends and understanding the spread of cancer.",
  },
];

const formulas = [
  {
    label: "Calculations I",
    title: "Crude Incidence Rate (CIR)",
    text: "Represents the total number of new cancer cases in a specified population during a year, relative to the size of that population.",
    numerator: "New Cancer Cases",
    denominator: "Total Population",
    multiplier: "x 10,00,000",
  },
  {
    label: "Calculations II",
    title: "Age-Specific Rate (ASpR)",
    text: "Provides incidence data for a specific age group, allowing for detailed demographic comparisons and risk assessment.",
    numerator: "Cases in Age Group",
    denominator: "Pop. of Age Group",
    multiplier: "x 10,00,000",
  },
  {
    label: "Calculations III",
    title: "Age Adjusted Rate (AAR)",
    text: "Allows cancer rates to be compared between populations with different age structures by applying age-specific rates to a standard population.",
    customFormula: (
      <div className="methods-complex-formula">
        <span>AAR = </span>
        <span className="methods-fraction methods-fraction-compact">
          <span>&Sigma; ASpR x standard population</span>
          <span>10,00,000</span>
        </span>
      </div>
    ),
  },
  {
    label: "Calculations IV",
    title: "Cumulative Risk",
    text: "Estimates the probability that a person will develop a particular cancer during a specific age period in the absence of other causes of death.",
    customFormula: (
      <div className="methods-linear-formula">
        Cumulative Risk = 100 x [1 - exp(-cumulative rate / 100)]
      </div>
    ),
  },
  {
    label: "Calculations V",
    title: "Truncated Age Adjusted Rate (TR)",
    text: "Similar to the age adjusted rate, but calculated for the truncated age group 35-64 years of age.",
    customFormula: (
      <div className="methods-linear-formula">
        TR = age adjusted rate for ages 35-64 years
      </div>
    ),
  },
  {
    label: "Calculations VI",
    title: "Simple Linear Regression",
    text: "Used for projecting cancer burden over time by estimating the relationship between an input feature and a predicted value.",
    customFormula: (
      <div className="methods-linear-formula">
        y = &theta;<sub>0</sub> + &theta;<sub>1</sub>x
      </div>
    ),
  },
];

const worldStandardPopulation = [
  ["0-4", "12,000"],
  ["5-9", "10,000"],
  ["10-14", "9,000"],
  ["15-19", "9,000"],
  // ["20-24", "8,000"],
  // ["25-29", "8,000"],
  // ["30-34", "6,000"],
  // ["35-39", "6,000"],
  // ["40-44", "6,000"],
  // ["45-49", "6,000"],
  // ["50-54", "5,000"],
  // ["55-59", "4,000"],
  // ["60-64", "4,000"],
  // ["65-69", "3,000"],
  // ["70-74", "2,000"],
  // ["75+", "2,000"],
  ["All Ages", "40,000"],
];

const populationEstimationRows = [
  ["0-4", "98416", "99335", "919", "0.006", "98", "99433"],
  ["5-9", "106147", "106655", "508", "0.004", "54", "106709"],
  ["10-14", "117440", "119951", "2512", "0.017", "267", "120218"],
  ["15-19", "123120", "120123", "-2997", "-0.021", "-319", "119805"],
  // ["20-24", "113176", "117208", "4032", "0.028", "429", "117637"],
  // ["25-29", "101493", "117557", "16064", "0.112", "1708", "119265"],
  // ["30-34", "90370", "104814", "14444", "0.100", "1535", "106350"],
  // ["35-39", "91914", "99672", "7758", "0.054", "825", "100496"],
  // ["40-44", "77995", "95004", "17008", "0.118", "1808", "96812"],
  // ["45-49", "74425", "89451", "15026", "0.105", "1597", "91048"],
  // ["50-54", "60822", "74103", "13282", "0.092", "1412", "75515"],
  // ["55-59", "45810", "61959", "16149", "0.112", "1717", "63676"],
  // ["60-64", "40652", "56149", "15496", "0.108", "1647", "57796"],
  // ["65-69", "26514", "36130", "9616", "0.067", "1022", "37153"],
  // ["70-74", "19904", "27452", "7548", "0.053", "802", "28254"],
  // ["75+", "20335", "26720", "6386", "0.044", "679", "27399"],
  ["Total", "445123", "446064", "942", "0.006", "100", "446165"],
];

const methodCards = [
  {
    icon: MdFunctions,
    title: "Difference Distribution Method",
    text: "This method estimates five-yearly age group populations using the age distribution from the two immediately preceding census years. It maintains both positive and negative growth across age groups while preserving the overall growth rate.",
    bullets: [
      "Uses census populations from 2001 and 2011 as the base reference.",
      "Calculates the difference for each age group and its share of total population change.",
      "Applies the difference proportion to estimate populations for a required year.",
    ],
  },
  {
    icon: FaProjectDiagram,
    title: "Cancer Projection",
    text: "Cancer projection can use a simple linear regression model to estimate future burden based on past trends. Each regression coefficient measures the expected change in the predicted value for a one-unit increase in the input feature.",
    bullets: [
      "y represents the predicted target value.",
      "x represents the input feature, such as year.",
      "The intercept and slope define the best-fit line.",
    ],
  },
];

const Methods = () => {
  return (
    <div className="methods-page">
      <section className="methods-hero">
        <h1>Methods &amp; Data Calculations</h1>
        <p>
          The Population-Based Childhood Cancer Registry (PBCCR) collects
          high-quality, standardized data on childhood cancers using the St.
          Jude Childhood Cancer Registry framework (SJCARES). The registry
          follows an active surveillance model to ensure comprehensive
          identification of all eligible childhood cancer cases among children
          and adolescents aged 0-19 years.
        </p>
        <p>
          Trained data managers (Social investigators) regularly visit
          participating hospitals and healthcare facilities to identify newly
          diagnosed cases. Multiple data sources are reviewed, including
          outpatient and inpatient records, oncology registries, pathology
          reports, laboratory records, imaging reports, and treatment logs. This
          multi-source approach helps ensure that all eligible cases of
          malignant tumors and benign central nervous system tumors are
          accurately captured.
        </p>
        <p>
          Each identified case is carefully verified using clinical,
          pathological, and diagnostic information before inclusion in the
          registry. Data collected include demographic details, diagnosis,
          staging, treatment, and outcomes.
        </p>
        <p>
          All information is securely entered into a REDCap-based database using
          the International Classification of Childhood Cancer, Third Edition
          (ICCC-3) coding system to ensure uniformity, accuracy, and
          international comparability of data.
        </p>
        <p>
          To maintain high data quality, the registry performs regular
          validation procedures, completeness checks, duplicate verification,
          and periodic audits. Strict measures are followed to protect patient
          privacy and confidentiality, including secure data systems and
          de-identification protocols.
        </p>
        <p>
          This systematic and collaborative approach enables the PBCCR to
          generate reliable and representative data on childhood cancer burden,
          supporting research, healthcare planning, policy development, and
          improvements in childhood cancer care.
        </p>
      </section>

      <section className="methods-overview" aria-label="Methods overview">
        {methodItems.map((item) => (
          <article className="methods-info-item" key={item.title}>
            <span className="methods-info-icon" aria-hidden="true">
              {React.createElement(item.icon as any)}
            </span>
            <div>
              <h2>{item.title}</h2>
              <p>{item.text}</p>
            </div>
          </article>
        ))}
      </section>

      <section className="methods-formulas" aria-label="Mathematical formulas">
        <div className="methods-section-heading">
          <h2>Standard Mathematical Formulas</h2>
          <p>
            Precise calculations used for generating standardized cancer
            statistics across all PBCCR registries.
          </p>
        </div>

        <div className="methods-formula-grid">
          {formulas.map((formula) => (
            <article className="methods-formula-card" key={formula.title}>
              <p className="methods-formula-label">{formula.label}</p>
              <h3>{formula.title}</h3>
              <p className="methods-formula-text">{formula.text}</p>
              <div
                className="methods-equation"
                aria-label={`${formula.title} formula`}
              >
                {formula.customFormula ? (
                  formula.customFormula
                ) : (
                  <>
                    <div className="methods-fraction">
                      <span>{formula.numerator}</span>
                      <span>{formula.denominator}</span>
                    </div>
                    <strong>{formula.multiplier}</strong>
                  </>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="methods-detail-section" aria-label="Advanced methods">
        <div className="methods-section-heading">
          <h2>Applied Statistical Methods</h2>
          <p>
            Population estimation and projection methods used for age-group
            analysis and future cancer burden estimates.
          </p>
        </div>

        <div className="methods-method-grid">
          {methodCards.map((card) => (
            <article className="methods-method-card" key={card.title}>
              <div className="methods-card-header">
                <span className="methods-info-icon" aria-hidden="true">
                  {React.createElement(card.icon as any)}
                </span>
                <span>
                  <h3>{card.title}</h3>
                </span>
              </div>
              <p>{card.text}</p>
              <ul>
                {card.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="methods-table-section" aria-label="Reference tables">
        <div className="methods-section-heading">
          <h2>Reference Tables</h2>
          <p>
            Supporting population references used in standardization and
            estimation workflows.
          </p>
        </div>

        <div className="methods-table-grid">
          <article className="methods-table-card">
            <div className="methods-table-title">
              {React.createElement(FaTable as any, { "aria-hidden": true })}
              <h3>Age Distribution of World Standard Population</h3>
            </div>
            <div className="methods-table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Age Group</th>
                    <th>World Standard Population</th>
                  </tr>
                </thead>
                <tbody>
                  {worldStandardPopulation.map(([ageGroup, population]) => (
                    <tr key={ageGroup}>
                      <td>{ageGroup}</td>
                      <td>{population}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>

          <article className="methods-table-card methods-table-card-wide">
            <div className="methods-table-title">
              {React.createElement(FaTable as any, { "aria-hidden": true })}
              <h3>Population Estimation by Five-Yearly Age Groups</h3>
            </div>
            <div className="methods-table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Age Groups</th>
                    <th>Census 2001</th>
                    <th>Census 2011</th>
                    <th>Difference</th>
                    <th>Difference Proportion</th>
                    <th>Estimated Growth</th>
                    <th>Estimated Population</th>
                  </tr>
                </thead>
                <tbody>
                  {populationEstimationRows.map((row) => (
                    <tr key={`${row[0]}-${row[1]}`}>
                      {row.map((cell, index) => (
                        <td key={`${row[0]}-${index}`}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
};

export default Methods;
